import type { MaybeN } from "@agrigate/ts-utils/lib/types";
import {
    Box,
    Button,
    HStack,
    Image,
    Progress,
    Text,
    VStack,
} from "@chakra-ui/react";
import { type ReactElement, useEffect, useRef } from "react";
import BackArrowIcon from "./assets/arrow-back-icon.svg?react";
import StarCoin from "./assets/images/star-coin.png";

const SurveyRunnerShell = (p: {
    formName: string;
    step: ReactElement | undefined;
    progressValue: number;
    progressLabel: string;
    isFirstStep: boolean;
    isLastStep: boolean;
    canAdvance: boolean;
    rewardCoins?: MaybeN<number>;
    onBack: () => void;
    onNext: () => void;
    onSubmit: () => void;
    onFlush: () => void;
}): ReactElement => {
    const {
        formName,
        step,
        progressValue,
        progressLabel,
        isFirstStep,
        isLastStep,
        canAdvance,
        rewardCoins,
        onBack,
        onNext,
        onSubmit,
        onFlush,
    } = p;

    const onFlushRef = useRef(onFlush);
    onFlushRef.current = onFlush;

    useEffect(() => {
        const flush = () => {
            if (document.visibilityState !== "hidden") return;
            onFlushRef.current();
        };
        document.addEventListener("visibilitychange", flush);
        window.addEventListener("pagehide", flush);
        return () => {
            document.removeEventListener("visibilitychange", flush);
            window.removeEventListener("pagehide", flush);
        };
    }, []);

    return (
        <>
            <HStack justifyContent={"space-between"} alignContent={"center"}>
                <BackArrowIcon height={20} width={20} />
                <Text
                    textAlign={"center"}
                    color={"black"}
                    fontFamily={"Instrument Sans"}
                    fontWeight={600}
                    fontSize={18}
                >
                    {formName}
                </Text>
                <Text>{progressLabel}</Text>
            </HStack>
            <VStack height={"90%"} justifyContent={"space-between"}>
                <VStack flexGrow={1} w={"100%"}>
                    <Progress.Root
                        colorPalette={"green"}
                        value={progressValue}
                        style={{ borderRadius: 20 }}
                        w={"100%"}
                        my={5}
                    >
                        <Progress.Track>
                            <Progress.Range />
                        </Progress.Track>
                        <Progress.Label />
                    </Progress.Root>
                    <Box w="100%">{step}</Box>
                </VStack>
                <VStack
                    w={"100%"}
                    borderTopWidth={1}
                    borderTopColor={"#CACACA"}
                    borderStyle={"dashed"}
                    pb={20}
                >
                    {rewardCoins && rewardCoins > 0 ? (
                        <HStack
                            backgroundColor={"#FFF9D8"}
                            borderColor={"#F0E9BF"}
                            borderWidth={1}
                            p={2}
                            borderRadius={100}
                            m={5}
                        >
                            <Text mb={0}>Finish this survey to earn</Text>
                            <Image src={StarCoin} />
                            <Text mb={0}>{rewardCoins}</Text>
                        </HStack>
                    ) : null}
                    <HStack w={"100%"} mt={4}>
                        <Button
                            type="button"
                            onClick={onBack}
                            disabled={isFirstStep}
                            flex={1}
                            p={5}
                            bgColor={"white"}
                            borderColor={"#C5C5C5"}
                            color={"#C5C5C5"}
                            borderWidth={1}
                        >
                            <Text color={"#C5C5C5"} m={0}>
                                {"< Back"}
                            </Text>
                        </Button>
                        {isLastStep ? (
                            <Button
                                flex={1}
                                type="button"
                                onClick={onSubmit}
                                bgColor={"#2A8645"}
                                color={"white"}
                                p={5}
                                disabled={!canAdvance}
                            >
                                Submit
                            </Button>
                        ) : (
                            <Button
                                flex={1}
                                type="button"
                                onClick={onNext}
                                bgColor={"#2A8645"}
                                color={"white"}
                                p={5}
                                disabled={!canAdvance}
                            >
                                {"Next >"}
                            </Button>
                        )}
                    </HStack>
                </VStack>
            </VStack>
        </>
    );
};

export default SurveyRunnerShell;
